import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { GLOBAL, POLICY_URL, deleteUrl, errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { searchUrl } from '../../pages/SoftwareBuilder/builderLogic';
import PatchPolicySection from './PatchPolicy';
import { SortHeader, useSort } from './sortable';

const list = (v) => (Array.isArray(v) ? v : []);
export const switchesUrl = () => `${POLICY_URL}?${new URLSearchParams({ account: GLOBAL, kind: 'uninstallswitch' }).toString()}`;
export const switchBody = ({ key, silentArguments, namePattern, note }) => ({
  account: GLOBAL, kind: 'uninstallswitch', key: key.trim(), silentArguments: silentArguments.trim(), namePattern: namePattern.trim(), note: note.trim(),
});
const when = (utc) => (utc ? String(utc).replace('T', ' ').slice(0, 16) : '');

// Add or change one uninstall switch. The program is a winget id: typed, or picked from a search of the software list.
function SwitchDialog({ initial, onSave, onClose }) {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const editing = Boolean(initial);
  const [form, setForm] = useState({ key: initial?.key || '', silentArguments: initial?.silentArguments || '', namePattern: initial?.namePattern || '', note: initial?.note || '' });
  const [text, setText] = useState('');
  const [found, setFound] = useState({ state: 'idle', packages: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    const q = text.trim();
    if (editing || q.length < 2) { setFound({ state: 'idle', packages: [] }); return undefined; }
    let cancelled = false;
    setFound((f) => ({ ...f, state: 'loading' }));
    const timer = setTimeout(() => {
      apiRef.current.request('GET', searchUrl(q))
        .then((r) => { if (!cancelled) setFound({ state: 'ok', packages: list(r.data?.packages).slice(0, 8) }); })
        .catch((ex) => { if (!cancelled) setFound({ state: 'error', packages: [], error: errorText(ex) }); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [text, editing]);

  const ready = form.key.trim() !== '' && form.silentArguments.trim() !== '';
  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(switchBody(form));
    } catch (ex) {
      setError(errorText(ex));
      setSaving(false);
    }
  };

  return (
    <div className="v2-dialog-host">
      <div className="v2-dialog" role="dialog" aria-label={editing ? 'Change uninstall switch' : 'Add uninstall switch'}>
        <h2>{editing ? 'Change uninstall switch' : 'Add uninstall switch'}</h2>
        <p className="v2-muted" style={{ marginTop: 0 }}>The text added to the program&apos;s own uninstall command so it runs without a window. It is used on every account.</p>
        {!editing && (
          <>
            <input className="v2-input" type="search" aria-label="Search software" placeholder="Search the software list to pick the program" value={text} onChange={(e) => setText(e.target.value)} />
            {found.state === 'error' && <div className="v2-alert err">{found.error}</div>}
            {found.packages.length > 0 && (
              <div role="list" aria-label="Search results">
                {found.packages.map((p) => (
                  <div role="listitem" key={p.id}>
                    <button type="button" className="v2-textbtn" onClick={() => { set({ key: p.id }); setText(''); }}>{`${p.name} (${p.id})`}</button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <label className="v2-field">Program id
          <input className="v2-input" aria-label="Program id" value={form.key} disabled={editing} placeholder="for example Docker.DockerDesktop" onChange={(e) => set({ key: e.target.value })} />
        </label>
        <label className="v2-field">Silent arguments
          <input className="v2-input" aria-label="Silent arguments" value={form.silentArguments} placeholder="for example --quiet or /S" onChange={(e) => set({ silentArguments: e.target.value })} />
        </label>
        <label className="v2-field">Name pattern (only for a program the software list does not have)
          <input className="v2-input" aria-label="Name pattern" value={form.namePattern} placeholder="for example ^Docker Desktop" onChange={(e) => set({ namePattern: e.target.value })} />
        </label>
        <label className="v2-field">Note
          <input className="v2-input" aria-label="Note" value={form.note} onChange={(e) => set({ note: e.target.value })} />
        </label>
        {error && <div className="v2-alert err" style={{ marginTop: 10 }}>{error}</div>}
        <div className="actions">
          <button type="button" className="v2-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="v2-btn primary" onClick={save} disabled={saving || !ready}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// Policy that is for every account (V2): today the uninstall switches, the text a removal adds to a program's own uninstall command so it is silent.
export default function GlobalPolicyPage() {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const [state, setState] = useState({ state: 'loading', items: [] });
  const [editing, setEditing] = useState(null);       // null, 'new' or the switch being changed
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const { sort, toggle, apply } = useSort();

  const load = useCallback(async () => {
    setState((s) => ({ ...s, state: 'loading' }));
    try {
      const r = await apiRef.current.request('GET', switchesUrl());
      setState({ state: 'ok', items: list(r.data?.uninstallSwitches) });
    } catch (ex) {
      setState({ state: 'error', error: errorText(ex), items: [] });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    await apiRef.current.request('POST', POLICY_URL, body);
    setEditing(null);
    setProblem('');
    await load();
  };
  const remove = async () => {
    setBusy(true);
    setProblem('');
    try {
      await apiRef.current.request('DELETE', deleteUrl(GLOBAL, deleting.id));
      setDeleting(null);
      await load();
    } catch (ex) {
      setProblem(errorText(ex));
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  const ordered = apply(state.items, {
    key: { get: (r) => r.key },
    silentArguments: { get: (r) => r.silentArguments },
    namePattern: { get: (r) => r.namePattern },
    updatedUtc: { get: (r) => r.updatedUtc },
  });

  return (
    <div>
      <div className="v2-head">
        <div>
          <h1>Global policy</h1>
          <p className="v2-muted">Settings that apply to every account.</p>
        </div>
      </div>
      <section className="v2-card" aria-label="Uninstall switches">
        <div className="v2-bar">
          <h2 className="v2-h2">Uninstall switches</h2>
          <button type="button" className="v2-btn primary" onClick={() => setEditing('new')}>Add uninstall switch</button>
        </div>
        <p className="v2-muted">Some programs register no silent uninstall command. When one of them is removed from a device, the switch below is added to its uninstall command. Detection, install and update are not affected.</p>
        {problem && <div className="v2-alert err" role="alert">{problem}</div>}
        {state.state === 'loading' && <div className="v2-loading"><span className="v2-spin" role="status" aria-label="Loading" /></div>}
        {state.state === 'error' && <div className="v2-alert err">{state.error}</div>}
        {state.state === 'ok' && (state.items.length === 0 ? <div className="v2-empty">No uninstall switches yet.</div> : (
          <table className="v2-table" aria-label="Uninstall switches">
            <thead>
              <tr>
                <SortHeader k="key" sort={sort} onSort={toggle}>Program</SortHeader>
                <SortHeader k="silentArguments" sort={sort} onSort={toggle}>Silent arguments</SortHeader>
                <SortHeader k="namePattern" sort={sort} onSort={toggle}>Name pattern</SortHeader>
                <SortHeader k="updatedUtc" sort={sort} onSort={toggle}>Changed</SortHeader>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {ordered.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="name" data-testid="switch-key" style={{ fontWeight: 700 }}>{r.key}</div>
                    {r.note && <div className="v2-sub">{r.note}</div>}
                  </td>
                  <td><code>{r.silentArguments}</code></td>
                  <td>{r.namePattern || ''}</td>
                  <td>{[when(r.updatedUtc), r.updatedBy].filter(Boolean).join(' by ')}</td>
                  <td className="nowrap">
                    <button type="button" className="v2-btn" style={{ marginRight: 6 }} aria-label={`Change ${r.key}`} onClick={() => setEditing(r)}>Change</button>
                    <button type="button" className="v2-btn" aria-label={`Delete ${r.key}`} onClick={() => setDeleting(r)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </section>

      <PatchPolicySection account={GLOBAL} />

      {editing && <SwitchDialog initial={editing === 'new' ? null : editing} onSave={save} onClose={() => setEditing(null)} />}
      {deleting && (
        <div className="v2-dialog-host">
          <div className="v2-dialog" role="dialog" aria-label="Delete uninstall switch">
            <h2>{`Delete the switch for ${deleting.key}?`}</h2>
            <p>Removing this program will no longer add the silent arguments, so it may not uninstall without a window.</p>
            <div className="actions">
              <button type="button" className="v2-btn" onClick={() => setDeleting(null)} disabled={busy}>Cancel</button>
              <button type="button" className="v2-btn primary" onClick={remove} disabled={busy}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
